import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  LightBulbIcon,
  CalendarIcon,
  FireIcon,
  ChartBarIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AIAnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [exams, setExams] = useState(null);
  const [currentExam, setCurrentExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [creatingProgram, setCreatingProgram] = useState(false);

  useEffect(() => {
    fetchAnalysis();
    fetchUserInfo();
  }, [id]);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserInfo(data.user);
    } catch (err) {
      console.error('User info fetch error:', err);
    }
  };

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch AI analysis with exam data
      const analysisResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/exams/${id}/ai-analysis`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (analysisResponse.data.success) {
        setAnalysis(analysisResponse.data.analysis);
        setExams(analysisResponse.data.exams);
        setCurrentExam(analysisResponse.data.exams.current);
        setFromCache(analysisResponse.data.fromCache || false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Analiz yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    alert('PDF export özelliği yakında eklenecek!');
  };

  const handleCreateProgram = async () => {
    try {
      setCreatingProgram(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/study-programs/create`,
        { examId: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Navigate to the created program
        navigate('/study-program');
      }
    } catch (err) {
      console.error('Program oluşturma hatası:', err);
      if (err.response?.status === 400) {
        alert(err.response.data.message || 'Zaten aktif bir programın var!');
      } else {
        alert('Program oluşturulurken bir hata oluştu. Lütfen tekrar dene.');
      }
    } finally {
      setCreatingProgram(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return '🔴';
      case 'medium':
        return '🟡';
      case 'low':
        return '🟢';
      default:
        return '⚪';
    }
  };

  const formatSubjectName = (key) => {
    const mapping = {
      'turkish': 'Türkçe',
      'math': 'Matematik',
      'mathAyt': 'Matematik (AYT)',
      'science': 'Fen Bilimleri',
      'social': 'Sosyal Bilimler',
      'physics': 'Fizik',
      'chemistry': 'Kimya',
      'biology': 'Biyoloji',
      'literature': 'Edebiyat',
      'history': 'Tarih',
      'geography': 'Coğrafya',
      'philosophy': 'Felsefe',
      'religion': 'Din',
      'language': 'Dil'
    };
    return mapping[key] || key;
  };

  // Prepare time series chart data
  const getTimeSeriesChartData = () => {
    if (!exams || !exams.current) return null;

    const examList = [];
    const labels = [];

    // Add exams in chronological order
    if (exams.first) {
      examList.push(exams.first);
      labels.push(`İlk Deneme\n${new Date(exams.first.examDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}`);
    }
    if (exams.previous && (!exams.first || exams.previous.id !== exams.first.id)) {
      examList.push(exams.previous);
      labels.push(`Önceki Deneme\n${new Date(exams.previous.examDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}`);
    }
    examList.push(exams.current);
    labels.push(`Güncel Deneme\n${new Date(exams.current.examDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}`);

    // Get all subjects from current exam
    const subjects = Object.keys(exams.current.nets || {});

    // Create dataset for each subject
    const datasets = subjects.map((subject, index) => {
      const colors = [
        { border: 'rgb(147, 51, 234)', bg: 'rgba(147, 51, 234, 0.1)' }, // Purple
        { border: 'rgb(59, 130, 246)', bg: 'rgba(59, 130, 246, 0.1)' }, // Blue
        { border: 'rgb(16, 185, 129)', bg: 'rgba(16, 185, 129, 0.1)' }, // Green
        { border: 'rgb(245, 158, 11)', bg: 'rgba(245, 158, 11, 0.1)' }, // Yellow
        { border: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)' }, // Red
        { border: 'rgb(236, 72, 153)', bg: 'rgba(236, 72, 153, 0.1)' }, // Pink
      ];
      const color = colors[index % colors.length];

      return {
        label: formatSubjectName(subject),
        data: examList.map(exam => exam.nets?.[subject] || 0),
        borderColor: color.border,
        backgroundColor: color.bg,
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: true
      };
    });

    return { labels, datasets };
  };

  // Get topic heatmap data
  const getTopicHeatmapData = () => {
    if (!exams || !exams.current || !exams.current.topicDetails) return [];

    return exams.current.topicDetails.map(detail => {
      const topicResults = detail.topicResults || [];
      return {
        subject: detail.subject,
        topics: topicResults.map(topic => {
          const net = Math.max(0, topic.correct - (topic.wrong / 4));
          const percentage = (net / topic.total) * 100;

          let status = 'low';
          if (percentage >= 70) status = 'high';
          else if (percentage >= 40) status = 'medium';

          return {
            name: topic.topicName,
            net: net.toFixed(1),
            correct: topic.correct,
            wrong: topic.wrong,
            empty: topic.empty,
            total: topic.total,
            percentage: percentage.toFixed(0),
            status
          };
        })
      };
    });
  };

  // Get target comparison data
  const getTargetComparisonData = () => {
    if (!exams || !exams.current || !userInfo || !userInfo.targetNets) return null;

    const currentNets = exams.current.nets || {};
    const targetNets = userInfo.targetNets;
    const examType = exams.current.examType;

    // Determine which target to use (TYT or AYT)
    let targets = {};
    if (examType.includes('TYT') && targetNets.tyt) {
      targets = targetNets.tyt;
    } else if (examType.includes('AYT') && targetNets.ayt) {
      targets = targetNets.ayt;
    }

    const comparison = [];
    Object.keys(targets).forEach(subject => {
      const target = targets[subject] || 0;
      const current = currentNets[subject] || 0;
      const percentage = target > 0 ? Math.min((current / target) * 100, 100) : 0;
      const gap = target - current;

      comparison.push({
        subject: formatSubjectName(subject),
        target,
        current,
        percentage: percentage.toFixed(1),
        gap: gap.toFixed(1),
        status: percentage >= 90 ? 'excellent' : percentage >= 70 ? 'good' : percentage >= 50 ? 'fair' : 'needs-work'
      });
    });

    return comparison;
  };

  const timeSeriesData = getTimeSeriesChartData();
  const heatmapData = getTopicHeatmapData();
  const targetComparison = getTargetComparisonData();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full mb-6 animate-pulse">
            <SparklesIcon className="w-10 h-10 text-purple-600 animate-spin" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            AI Analiz Ediyor...
          </h3>
          <p className="text-gray-600">
            Deneme sonuçlarınız yapay zeka tarafından değerlendiriliyor
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
            <ArrowLeftIcon className="w-10 h-10 text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Bir Hata Oluştu
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 transition"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      {/* Header - Print Hidden */}
      <div className="bg-white border-b border-gray-200 print:hidden sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                <span className="font-medium">Geri</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  AI Performans Analizi
                </h1>
                {currentExam && (
                  <p className="text-sm text-gray-600">
                    {currentExam.examType} - {new Date(currentExam.examDate).toLocaleDateString('tr-TR')}
                  </p>
                )}
              </div>
              {fromCache && (
                <span className="ml-3 px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  ⚡ Önbellekten
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
                <span>PDF İndir</span>
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition"
              >
                <PrinterIcon className="w-5 h-5" />
                <span>Yazdır</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {analysis && (
          <div className="space-y-8">
            {/* Hero Section - AI Summary */}
            <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl shadow-2xl p-8 text-white">
              <div className="flex items-start gap-6">
                <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                  <SparklesIcon className="w-12 h-12" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold mb-4">Genel Değerlendirme</h2>
                  <p className="text-lg text-purple-50 leading-relaxed">
                    {analysis.summary}
                  </p>
                </div>
              </div>
            </div>

            {/* Time Series Progress Chart */}
            {timeSeriesData && timeSeriesData.datasets.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <ChartBarIcon className="w-8 h-8 text-purple-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      İlerleme Grafiği
                    </h3>
                    <p className="text-sm text-gray-600">
                      Denemeler arası net karşılaştırması
                    </p>
                  </div>
                </div>
                <div className="h-96">
                  <Line
                    data={timeSeriesData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top',
                          labels: {
                            usePointStyle: true,
                            padding: 15,
                            font: { size: 12, weight: '600' }
                          }
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          titleFont: { size: 14, weight: 'bold' },
                          bodyFont: { size: 13 },
                          padding: 12,
                          cornerRadius: 8,
                          callbacks: {
                            label: function(context) {
                              return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} net`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(0, 0, 0, 0.05)'
                          },
                          ticks: {
                            font: { size: 12 }
                          },
                          title: {
                            display: true,
                            text: 'Net Sayısı',
                            font: { size: 13, weight: 'bold' }
                          }
                        },
                        x: {
                          grid: {
                            display: false
                          },
                          ticks: {
                            font: { size: 11 }
                          }
                        }
                      },
                      interaction: {
                        mode: 'index',
                        intersect: false
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Topic Heatmap */}
            {heatmapData && heatmapData.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <FireIcon className="w-8 h-8 text-orange-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      Konu Bazlı Performans Haritası
                    </h3>
                    <p className="text-sm text-gray-600">
                      Hangi konularda güçlü, hangi konularda çalışman gerekiyor?
                    </p>
                  </div>
                </div>
                <div className="space-y-6">
                  {heatmapData.map((subjectData, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">
                        {subjectData.subject}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {subjectData.topics.map((topic, topicIndex) => (
                          <div
                            key={topicIndex}
                            className={`p-4 rounded-lg border-2 ${
                              topic.status === 'high'
                                ? 'bg-green-50 border-green-300'
                                : topic.status === 'medium'
                                ? 'bg-yellow-50 border-yellow-300'
                                : 'bg-red-50 border-red-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900 text-sm">
                                {topic.name}
                              </span>
                              <span
                                className={`text-xs font-bold px-2 py-1 rounded ${
                                  topic.status === 'high'
                                    ? 'bg-green-200 text-green-800'
                                    : topic.status === 'medium'
                                    ? 'bg-yellow-200 text-yellow-800'
                                    : 'bg-red-200 text-red-800'
                                }`}
                              >
                                {topic.percentage}%
                              </span>
                            </div>
                            <div className="text-xs text-gray-700">
                              <span className="font-bold">{topic.net} net</span> • D:{topic.correct} Y:{topic.wrong} B:{topic.empty}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Target Comparison */}
            {targetComparison && targetComparison.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <TrophyIcon className="w-8 h-8 text-yellow-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      Hedef Karşılaştırması
                    </h3>
                    <p className="text-sm text-gray-600">
                      Hedef netlerinize ne kadar yakınsınız?
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  {targetComparison.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-bold text-gray-900">{item.subject}</span>
                          <span className="ml-3 text-sm text-gray-600">
                            {item.current} / {item.target} net
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.gap > 0 ? (
                            <span className="text-sm font-medium text-orange-600">
                              {item.gap} net eksik
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-green-600">
                              ✓ Hedefe ulaştın!
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            item.status === 'excellent'
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                              : item.status === 'good'
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                              : item.status === 'fair'
                              ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                              : 'bg-gradient-to-r from-red-500 to-pink-500'
                          }`}
                          style={{ width: `${item.percentage}%` }}
                        >
                          <span className="flex items-center justify-end h-full pr-2 text-xs font-bold text-white">
                            {item.percentage}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Improvements & Declines Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Improvements */}
              {analysis.improvements && analysis.improvements.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-green-500">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <ArrowTrendingUpIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      İlerleme Gösteren Konular
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {analysis.improvements.map((item, index) => (
                      <li key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                        <span className="text-green-500 text-xl mt-0.5">✓</span>
                        <span className="text-gray-800 flex-1">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Declines */}
              {analysis.declines && analysis.declines.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-red-500">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-red-100 p-3 rounded-lg">
                      <ArrowTrendingDownIcon className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Dikkat Edilmesi Gereken Konular
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {analysis.declines.map((item, index) => (
                      <li key={index} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                        <span className="text-red-500 text-xl mt-0.5">⚠</span>
                        <span className="text-gray-800 flex-1">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Recommendations */}
            {analysis.recommendations && analysis.recommendations.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <LightBulbIcon className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Kişiselleştirilmiş Öneriler
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {analysis.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className={`border-2 rounded-xl p-6 ${getPriorityColor(rec.priority)}`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="text-3xl">{getPriorityIcon(rec.priority)}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3 flex-wrap">
                            <h4 className="text-lg font-bold">{rec.subject}</h4>
                            {rec.topic && (
                              <span className="text-sm px-3 py-1 bg-white/60 rounded-full font-medium">
                                {rec.topic}
                              </span>
                            )}
                            <span className="text-xs px-2 py-1 rounded-full bg-white/50 ml-auto">
                              {rec.priority === 'high' ? 'Yüksek Öncelik' :
                               rec.priority === 'medium' ? 'Orta Öncelik' : 'Düşük Öncelik'}
                            </span>
                          </div>
                          <p className="text-sm leading-relaxed">
                            {rec.advice}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Target Progress */}
            {analysis.targetProgress && (
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                    <FireIcon className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-4">
                      Hedefinize Ne Kadar Yakınsınız?
                    </h3>
                    <p className="text-indigo-50 leading-relaxed text-lg">
                      {analysis.targetProgress}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Motivation */}
            {analysis.motivation && (
              <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl shadow-lg p-8 text-white">
                <div className="flex items-start gap-4">
                  <span className="text-5xl">💪</span>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-4">Motivasyon</h3>
                    <p className="text-pink-50 leading-relaxed text-xl">
                      {analysis.motivation}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Create Study Program CTA */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-2xl p-10 text-white text-center">
              <div className="max-w-2xl mx-auto">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                  <CalendarIcon className="w-10 h-10" />
                </div>
                <h3 className="text-3xl font-bold mb-4">
                  Detaylı Çalışma Programı Oluştur
                </h3>
                <p className="text-emerald-50 text-lg mb-8 leading-relaxed">
                  Bu analiz sonuçlarına göre sana özel, günlük takip edilebilir,
                  konu bazlı detaylı bir çalışma programı oluşturalım mı?
                </p>
                <button
                  onClick={handleCreateProgram}
                  disabled={creatingProgram}
                  className="inline-flex items-center gap-3 bg-white text-emerald-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-50 transition shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingProgram ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                      Program Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <CalendarIcon className="w-6 h-6" />
                      Program Oluştur
                    </>
                  )}
                </button>
                <p className="text-emerald-100 text-sm mt-4">
                  ✨ AI destekli kişiselleştirilmiş program
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AIAnalysisPage;
